import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderData = location?.state?.orderData;

  useEffect(() => {
    // If no order data, redirect to homepage
    if (!orderData) {
      navigate('/');
    }
  }, [orderData, navigate]);

  if (!orderData) {
    return null;
  }

  const orderNumber = `INF-${Date.now()?.toString()?.slice(-8)}`;
  const orderDate = new Date(orderData?.orderDate)?.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <>
      <Helmet>
        <title>Order Confirmation - INF LABS</title>
        <meta name="description" content="Your order has been received" />
      </Helmet>

      <div className="min-h-screen bg-[var(--color-background)]">
        <Header />
        
        <main className="pt-[100px] pb-12 px-4 md:px-6 lg:px-8">
          <div className="max-w-[800px] mx-auto">
            {/* Success Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-accent/20 mb-4">
                <Icon name="CheckCircle" size={48} color="var(--color-accent)" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
                Order Received!
              </h1>
              <p className="text-lg text-muted-foreground">
                Thank you for your purchase. Your order has been received and will be processed shortly.
              </p>
            </div>

            {/* Order Details */}
            <div className="p-6 rounded-xl bg-card border border-border mb-6">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <Icon name="FileText" size={24} color="var(--color-primary)" />
                Order Details
              </h2>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Order Number</p>
                  <p className="text-base font-semibold text-foreground">{orderNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Order Date</p>
                  <p className="text-base font-semibold text-foreground">{orderDate}</p>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <h3 className="text-lg font-semibold text-foreground mb-3">Items Ordered</h3>
                <div className="space-y-3">
                  {orderData?.items?.map((item) => (
                    <div key={item?.id} className="flex items-center gap-4 p-3 rounded-lg bg-muted/50">
                      <img 
                        src={item?.image} 
                        alt={item?.imageAlt || item?.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h4 className="text-base font-semibold text-foreground">{item?.name}</h4>
                        <p className="text-sm text-muted-foreground">Quantity: {item?.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-base font-bold text-foreground">
                          ${(item?.price * item?.quantity)?.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-border mt-4 pt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-foreground">Total</span>
                  <span className="text-primary">${orderData?.total?.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Shipping Information */}
            <div className="p-6 rounded-xl bg-card border border-border mb-6">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <Icon name="MapPin" size={24} color="var(--color-primary)" />
                Shipping Information
              </h2>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Name</p>
                  <p className="text-base text-foreground">{orderData?.fullName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Address</p>
                  <p className="text-base text-foreground whitespace-pre-line">{orderData?.fullAddress}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Phone</p>
                  <p className="text-base text-foreground">{orderData?.phoneNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Email</p>
                  <p className="text-base text-foreground">{orderData?.email}</p>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="p-6 rounded-xl bg-primary/10 border border-primary/30 mb-6">
              <div className="flex items-start gap-4">
                <Icon name="MessageSquare" size={32} color="var(--color-primary)" className="flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-bold text-primary mb-2">
                    Important: Confirm Your Order
                  </h3>
                  <p className="text-foreground mb-3">
                    To complete your order, please text the following information to:
                  </p>
                  <p className="text-2xl font-bold text-primary mb-3">
                    801-810-4671
                  </p>
                  <div className="bg-card/50 p-4 rounded-lg mb-3">
                    <p className="text-sm text-foreground font-mono whitespace-pre-line">
                      {`Full Name: ${orderData?.fullName}
Address: ${orderData?.fullAddress}
Phone: ${orderData?.phoneNumber}
Products: ${orderData?.items?.map(item => item?.name)?.join(', ')}`}
                    </p>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Copy the information above and send it via text message to confirm your order.
                  </p>
                </div>
              </div>
            </div>

            {/* Shipping Notice */}
            <div className="p-6 rounded-xl bg-accent/10 border border-accent/30 mb-8">
              <div className="flex items-start gap-4">
                <Icon name="Truck" size={32} color="var(--color-accent)" className="flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-bold text-accent mb-2">
                    Next Day Shipping
                  </h3>
                  <p className="text-foreground">
                    Your order will be shipped the next business day after we receive your text confirmation and payment verification.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => navigate('/')}
                variant="outline"
                className="flex-1"
              >
                <Icon name="Home" size={20} className="mr-2" />
                Return to Homepage
              </Button>
              <Button 
                onClick={() => navigate('/product-catalog')}
                variant="default"
                className="flex-1"
              >
                <Icon name="ShoppingBag" size={20} className="mr-2" />
                Continue Shopping
              </Button>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default OrderConfirmation;